import { computed, onScopeDispose, ref, unref, watch } from "vue";
import {
  CONTENT_SUBSCRIPTION_CADENCES,
  FOLLOW_DIGEST_CONSENT_VERSION,
  contentSubscriptionService,
} from "@/services/contentSubscriptionService";
import {
  createFollowingAuthorityOperationId,
  followingAuthorityService,
} from "@/services/followingAuthorityService";
import {
  isFollowingAuthorityCommandsFeatureEnabled,
  isFollowingAuthorityReadsFeatureEnabled,
} from "@/config/runtimeConfig";

export const DEFAULT_FOLLOW_DIGEST_CADENCE = "weekly";
export const FOLLOW_STATUS_MESSAGE_DURATION_MS = 4000;

const LOAD_ERROR_MESSAGE =
  "We couldn't load this follow right now. Please try again.";
const UPDATE_ERROR_MESSAGE =
  "We couldn't update this follow right now. Please try again.";
const SESSION_ERROR_MESSAGE = "Your session ended. Sign in and try again.";

const registriesByReadService = new WeakMap();
const legacyCommandRegistryKey = Object.freeze({});

function registryFor(service, authorityService) {
  let registriesByAuthorityService = registriesByReadService.get(service);
  if (!registriesByAuthorityService) {
    registriesByAuthorityService = new WeakMap();
    registriesByReadService.set(service, registriesByAuthorityService);
  }
  let registry = registriesByAuthorityService.get(authorityService);
  if (!registry) {
    registry = new Map();
    registriesByAuthorityService.set(authorityService, registry);
  }
  return registry;
}

function registryKey({
  currentUserUid,
  targetType,
  targetId,
  targetAuthorityPublicIdentifier,
  authorityReadsEnabled,
  authorityCommandsEnabled,
}) {
  return JSON.stringify([
    currentUserUid,
    targetType,
    targetId,
    targetAuthorityPublicIdentifier || "",
    authorityReadsEnabled,
    authorityCommandsEnabled,
  ]);
}

function getRegistryEntry(service, authorityService, context) {
  const commandRegistryKey =
    context.authorityReadsEnabled || context.authorityCommandsEnabled
    ? authorityService
    : legacyCommandRegistryKey;
  const registry = registryFor(service, commandRegistryKey);
  const key = registryKey(context);
  let entry = registry.get(key);
  if (!entry) {
    entry = {
      key,
      registry,
      state: null,
      loading: false,
      ready: false,
      authorityRevision: null,
      operationPromise: null,
      subscribers: new Set(),
    };
    registry.set(key, entry);
  }
  return entry;
}

function notifyRegistryEntry(entry) {
  entry.subscribers.forEach((subscriber) => subscriber(entry));
}

function cleanupRegistryEntry(entry) {
  if (
    entry.subscribers.size === 0 &&
    !entry.loading &&
    !entry.operationPromise
  ) {
    entry.registry.delete(entry.key);
  }
}

function readReactiveValue(source) {
  return typeof source === "function" ? source() : unref(source);
}

function invokeService(operation) {
  try {
    return Promise.resolve(operation());
  } catch (error) {
    return Promise.reject(error);
  }
}

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (_error) {
    return "UTC";
  }
}

function browserLocale() {
  return typeof navigator !== "undefined" && navigator.language
    ? navigator.language
    : "en";
}

/**
 * Own one target's Follow state in the nearest shared parent. Pass the returned
 * refs/actions to every identity and menu control for that target.
 * `targetAuthorityPublicIdentifier` must come from a private-authority read,
 * never from a legacy/default-Firestore presentation document.
 */
export function useContentSubscription({
  targetType,
  targetId,
  targetAuthorityPublicIdentifier,
  currentUserUid,
  requireAuth,
  enabled = true,
  service = contentSubscriptionService,
  authorityReadsEnabled = isFollowingAuthorityReadsFeatureEnabled,
  authorityCommandsEnabled = isFollowingAuthorityCommandsFeatureEnabled,
  authorityService = followingAuthorityService,
  operationIdFactory = createFollowingAuthorityOperationId,
  getTimeZone = browserTimeZone,
  getLocale = browserLocale,
} = {}) {
  const following = ref(false);
  const isLoading = ref(false);
  const isReady = ref(false);
  const requiresDigestSetup = ref(false);
  const digestStatus = ref(null);
  const cadence = ref(null);
  const disclosureOpen = ref(false);
  const selectedCadence = ref(DEFAULT_FOLLOW_DIGEST_CADENCE);
  const errorMessage = ref("");
  const statusMessage = ref("");

  let requestGeneration = 0;
  let loadedContextKey = null;
  let activeRegistryEntry = null;
  let statusMessageTimer = null;

  const clearStatusMessage = () => {
    if (statusMessageTimer !== null) {
      clearTimeout(statusMessageTimer);
      statusMessageTimer = null;
    }
    statusMessage.value = "";
  };

  const setTransientStatusMessage = (message) => {
    clearStatusMessage();
    statusMessage.value = message;
    statusMessageTimer = setTimeout(() => {
      statusMessage.value = "";
      statusMessageTimer = null;
    }, FOLLOW_STATUS_MESSAGE_DURATION_MS);
  };

  const readContext = () => {
    const rawTargetId = readReactiveValue(targetId);
    const rawPublicIdentifier = readReactiveValue(
      targetAuthorityPublicIdentifier,
    );
    return {
      enabled: readReactiveValue(enabled) === true,
      authorityReadsEnabled:
        readReactiveValue(authorityReadsEnabled) === true,
      authorityCommandsEnabled:
        readReactiveValue(authorityCommandsEnabled) === true,
      targetType: readReactiveValue(targetType),
      targetId:
        typeof rawTargetId === "string" ? rawTargetId.trim() : rawTargetId,
      targetAuthorityPublicIdentifier:
        typeof rawPublicIdentifier === "string"
          ? rawPublicIdentifier.trim()
          : rawPublicIdentifier,
      currentUserUid: readReactiveValue(currentUserUid),
    };
  };

  const contextKey = ({
    enabled: featureEnabled,
    targetType: type,
    targetId: id,
    targetAuthorityPublicIdentifier: publicIdentifier,
    currentUserUid: uid,
    authorityReadsEnabled: privateReadsEnabled,
    authorityCommandsEnabled: privateCommandsEnabled,
  }) => JSON.stringify([
    featureEnabled,
    uid || "",
    type || "",
    id || "",
    publicIdentifier || "",
    privateReadsEnabled,
    privateCommandsEnabled,
  ]);

  const hasTarget = ({ targetType: type, targetId: id }) =>
    (type === "user" || type === "organisation") &&
    typeof id === "string" &&
    id.length > 0;

  const hasResolvableTarget = (context) =>
    hasTarget(context) &&
    (!(context.authorityReadsEnabled || context.authorityCommandsEnabled) ||
      (typeof context.targetAuthorityPublicIdentifier === "string" &&
        context.targetAuthorityPublicIdentifier.length > 0)) &&
    (!context.authorityCommandsEnabled || context.authorityReadsEnabled);

  const authorityTarget = (context) => Object.freeze({
    type: context.targetType === "user" ? "account" : "organisation",
    publicIdentifier: context.targetAuthorityPublicIdentifier,
  });

  const canOwnFollowState = (context) =>
    context.enabled &&
    hasResolvableTarget(context) &&
    Boolean(context.currentUserUid) &&
    !(
      context.targetType === "user" &&
      context.targetId === context.currentUserUid
    );

  const isSelfTarget = computed(() => {
    const context = readContext();
    return Boolean(
      context.currentUserUid &&
        context.targetType === "user" &&
        context.targetId === context.currentUserUid,
    );
  });

  const resetCanonicalState = () => {
    following.value = false;
    requiresDigestSetup.value = false;
    digestStatus.value = null;
    cadence.value = null;
    disclosureOpen.value = false;
    selectedCadence.value = DEFAULT_FOLLOW_DIGEST_CADENCE;
    errorMessage.value = "";
    clearStatusMessage();
  };

  const applyCanonicalState = (state) => {
    following.value = state.following;
    requiresDigestSetup.value = state.requiresDigestSetup;
    digestStatus.value = state.digestStatus;
    cadence.value = state.cadence;
    if (CONTENT_SUBSCRIPTION_CADENCES.includes(state.cadence)) {
      selectedCadence.value = state.cadence;
    }
  };

  const syncFromRegistry = (entry) => {
    if (entry !== activeRegistryEntry) return;
    isLoading.value = entry.loading;
    isReady.value = entry.ready;
    if (entry.state) {
      applyCanonicalState(entry.state);
      loadedContextKey = contextKey(readContext());
      if (following.value || !requiresDigestSetup.value) {
        disclosureOpen.value = false;
      }
    } else {
      loadedContextKey = null;
    }
  };

  const detachRegistryEntry = () => {
    if (!activeRegistryEntry) return;
    const previousEntry = activeRegistryEntry;
    previousEntry.subscribers.delete(syncFromRegistry);
    activeRegistryEntry = null;
    cleanupRegistryEntry(previousEntry);
  };

  const attachRegistryEntry = (context) => {
    const key = registryKey(context);
    if (activeRegistryEntry?.key === key) return activeRegistryEntry;
    detachRegistryEntry();
    const entry = getRegistryEntry(service, authorityService, context);
    activeRegistryEntry = entry;
    entry.subscribers.add(syncFromRegistry);
    syncFromRegistry(entry);
    return entry;
  };

  const setRegistryLoading = (entry, loading) => {
    entry.loading = loading;
    notifyRegistryEntry(entry);
  };

  const isCurrentRequest = (token, requestedContext) =>
    requestGeneration === token &&
    contextKey(readContext()) === contextKey(requestedContext);

  const loadContext = async (requestedContext, { force = false } = {}) => {
    const token = ++requestGeneration;
    const entry = attachRegistryEntry(requestedContext);
    errorMessage.value = "";
    clearStatusMessage();

    if (entry.state && entry.ready && !force) {
      syncFromRegistry(entry);
      return entry.state;
    }

    if (!entry.operationPromise) {
      entry.ready = false;
      setRegistryLoading(entry, true);
      const operationPromise = requestedContext.authorityReadsEnabled
        ? invokeService(() =>
            authorityService.getOwnFollowing(
              authorityTarget(requestedContext),
              { expectedActorUid: requestedContext.currentUserUid },
            ),
          ).then((result) => {
          entry.authorityRevision = result.revision;
          return Object.freeze({
            targetType: requestedContext.targetType,
            targetId: requestedContext.targetId,
            following: result.following,
            requiresDigestSetup: false,
            digestStatus: null,
            cadence: null,
          });
          })
        : invokeService(() =>
            service.getContentSubscription({
              targetType: requestedContext.targetType,
              targetId: requestedContext.targetId,
            }),
          );
      entry.operationPromise = operationPromise;

      operationPromise
        .then((state) => {
          entry.state = state;
          entry.ready = true;
          notifyRegistryEntry(entry);
        })
        .catch(() => {
          entry.ready = true;
          notifyRegistryEntry(entry);
        })
        .finally(() => {
          if (entry.operationPromise === operationPromise) {
            entry.operationPromise = null;
          }
          setRegistryLoading(entry, false);
          cleanupRegistryEntry(entry);
        });
    }

    const operationPromise = entry.operationPromise;

    try {
      const state = await operationPromise;
      if (!isCurrentRequest(token, requestedContext)) return null;

      entry.state = state;
      entry.ready = true;
      notifyRegistryEntry(entry);
      loadedContextKey = contextKey(requestedContext);
      isReady.value = true;
      return state;
    } catch (error) {
      if (!isCurrentRequest(token, requestedContext)) return null;

      loadedContextKey = null;
      if (error?.code === "unauthenticated") {
        disclosureOpen.value = false;
        errorMessage.value = SESSION_ERROR_MESSAGE;
        presentAuthentication();
      } else {
        errorMessage.value = LOAD_ERROR_MESSAGE;
      }
      isReady.value = true;
      return null;
    }
  };

  const refresh = async ({ force = true } = {}) => {
    const context = readContext();
    if (!canOwnFollowState(context)) {
      ++requestGeneration;
      loadedContextKey = null;
      detachRegistryEntry();
      isLoading.value = false;
      resetCanonicalState();
      isReady.value = true;
      return null;
    }
    return loadContext(context, { force });
  };

  const presentAuthentication = () => {
    if (typeof requireAuth === "function") {
      try {
        Promise.resolve(requireAuth()).catch(() => {});
      } catch (_error) {
        // Authentication presentation is caller-owned; Follow state stays inert.
      }
    }
  };

  const askForAuthentication = () => {
    if (readContext().currentUserUid) return true;
    presentAuthentication();
    return false;
  };

  const setSuccessMessage = (state) => {
    let message;
    if (!state.following) {
      message = "Unfollowed.";
    } else if (state.digestStatus === "paused") {
      message = "Following. Digest emails remain paused.";
    } else if (state.digestStatus === "pendingVerification") {
      message =
        "Following. Verify your email before digest emails can start.";
    } else if (state.requiresDigestSetup || state.digestStatus === null) {
      message = "Following.";
    } else {
      message = "Following. Digest emails are enabled.";
    }
    setTransientStatusMessage(message);
  };

  const mutateFollowing = async ({ nextFollowing, digestSetup }) => {
    if (isLoading.value) return false;

    const requestedContext = readContext();
    if (
      !hasResolvableTarget(requestedContext) ||
      !requestedContext.currentUserUid
    ) {
      return false;
    }

    const token = ++requestGeneration;
    const entry = attachRegistryEntry(requestedContext);
    setRegistryLoading(entry, true);
    errorMessage.value = "";
    clearStatusMessage();

    const operationPromise = invokeService(() => {
      if (requestedContext.authorityReadsEnabled) {
        if (!requestedContext.authorityCommandsEnabled) {
          throw Object.assign(
            new Error("Following commands are not enabled."),
            { code: "feature-disabled" },
          );
        }
        if (digestSetup) {
          throw Object.assign(
            new Error("Digest setup is not part of the Following authority command."),
            { code: "failed-precondition" },
          );
        }
        return authorityService.setOwnFollowing(
          {
            operationId: operationIdFactory(),
            expectedRevision: entry.authorityRevision ?? 0,
            target: authorityTarget(requestedContext),
            following: nextFollowing,
          },
          { expectedActorUid: requestedContext.currentUserUid },
        );
      }
      const request = {
        targetType: requestedContext.targetType,
        targetId: requestedContext.targetId,
        following: nextFollowing,
      };
      if (digestSetup) request.digestSetup = digestSetup;
      return service.setContentSubscription(request);
    });
    entry.operationPromise = operationPromise;

    try {
      const result = await operationPromise;
      const state = requestedContext.authorityCommandsEnabled
        ? { ...entry.state, following: result.following }
        : result;
      if (requestedContext.authorityCommandsEnabled) {
        entry.authorityRevision = result.revision;
      }
      entry.state = state;
      entry.ready = true;
      notifyRegistryEntry(entry);
      if (!isCurrentRequest(token, requestedContext)) return false;

      loadedContextKey = contextKey(requestedContext);
      disclosureOpen.value = false;
      isReady.value = true;
      setSuccessMessage(state);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("social:following-changed"));
      }
      return true;
    } catch (error) {
      if (!isCurrentRequest(token, requestedContext)) return false;

      if (
        error?.code === "aborted" &&
        !requestedContext.authorityCommandsEnabled
      ) {
        disclosureOpen.value = false;
        loadedContextKey = null;
        if (entry.operationPromise === operationPromise) {
          entry.operationPromise = null;
        }
        setRegistryLoading(entry, false);
        const refreshedState = await loadContext(requestedContext, {
          force: true,
        });
        if (
          refreshedState &&
          contextKey(readContext()) === contextKey(requestedContext)
        ) {
          errorMessage.value = "";
          setTransientStatusMessage(
            refreshedState.following
              ? "Following with your current digest settings."
              : "Digest settings changed. Select Follow again.",
          );
        }
        return false;
      }

      if (
        error?.code === "unauthenticated" ||
        error?.code === "stale-auth-context"
      ) {
        loadedContextKey = null;
        disclosureOpen.value = false;
        errorMessage.value = SESSION_ERROR_MESSAGE;
        clearStatusMessage();
        presentAuthentication();
        return false;
      }

      errorMessage.value = UPDATE_ERROR_MESSAGE;
      clearStatusMessage();
      return false;
    } finally {
      if (entry.operationPromise === operationPromise) {
        entry.operationPromise = null;
      }
      setRegistryLoading(entry, false);
      cleanupRegistryEntry(entry);
    }
  };

  const requestToggle = async () => {
    if (
      !readContext().enabled ||
      !askForAuthentication() ||
      isLoading.value ||
      isSelfTarget.value ||
      (readContext().authorityReadsEnabled &&
        !readContext().authorityCommandsEnabled)
    ) {
      return false;
    }

    let context = readContext();
    if (!hasResolvableTarget(context)) return false;

    if (loadedContextKey !== contextKey(context)) {
      const state = await loadContext(context);
      if (!state) return false;
      context = readContext();
      if (loadedContextKey !== contextKey(context)) return false;
    }

    errorMessage.value = "";
    clearStatusMessage();
    // Follow is a social graph action. Email digest consent is intentionally
    // independent and can be configured later from the account controls.
    return mutateFollowing({ nextFollowing: !following.value });
  };

  const completeFirstFollow = async (enabled) => {
    if (
      !readContext().enabled ||
      !askForAuthentication() ||
      isLoading.value ||
      !disclosureOpen.value ||
      following.value ||
      !requiresDigestSetup.value
    ) {
      return false;
    }

    const selected = CONTENT_SUBSCRIPTION_CADENCES.includes(
      selectedCadence.value,
    )
      ? selectedCadence.value
      : DEFAULT_FOLLOW_DIGEST_CADENCE;
    selectedCadence.value = selected;

    let timezone = "UTC";
    let locale = "en";
    try {
      timezone = getTimeZone() || "UTC";
    } catch (_error) {
      timezone = "UTC";
    }
    try {
      locale = getLocale() || "en";
    } catch (_error) {
      locale = "en";
    }

    return mutateFollowing({
      nextFollowing: true,
      digestSetup: {
        enabled,
        cadence: selected,
        timezone,
        locale,
        consentVersion: FOLLOW_DIGEST_CONSENT_VERSION,
      },
    });
  };

  const confirmDigestSetup = () => completeFirstFollow(true);
  const followWithoutEmail = () => completeFirstFollow(false);

  const cancelDigestSetup = () => {
    if (isLoading.value) return false;
    disclosureOpen.value = false;
    errorMessage.value = "";
    clearStatusMessage();
    return true;
  };

  watch(
    [
      () => readReactiveValue(targetType),
      () => readReactiveValue(targetId),
      () => readReactiveValue(targetAuthorityPublicIdentifier),
      () => readReactiveValue(currentUserUid),
      () => readReactiveValue(enabled),
      () => readReactiveValue(authorityReadsEnabled),
      () => readReactiveValue(authorityCommandsEnabled),
    ],
    () => {
      ++requestGeneration;
      loadedContextKey = null;
      detachRegistryEntry();
      isLoading.value = false;
      isReady.value = false;
      resetCanonicalState();
      void refresh({ force: false });
    },
    { immediate: true, flush: "sync" },
  );

  onScopeDispose(() => {
    clearStatusMessage();
    detachRegistryEntry();
  });

  return {
    following,
    isLoading,
    isReady,
    requiresDigestSetup,
    digestStatus,
    cadence,
    disclosureOpen,
    selectedCadence,
    errorMessage,
    statusMessage,
    isSelfTarget,
    refresh,
    requestToggle,
    confirmDigestSetup,
    followWithoutEmail,
    cancelDigestSetup,
  };
}
